import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link
} from '@mui/material';
import {
  Extension,
  Login,
  GetApp,
  AddCircle,
  Info,
  CheckCircle
} from '@mui/icons-material';

const LinkedInAccountGuide = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h5" component="div" fontWeight="bold">
          How to Add a LinkedIn Account to Your Profile
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          Prerequisites
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <Extension color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Install the Cookie Editor extension for Chrome from the Chrome Web Store"
              secondary={
                <Link
                  href="https://chromewebstore.google.com/detail/cookie-editor/hlkenndednhfkekhgcdicdfddnkalmdm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Cookie Editor Extension Link
                </Link>
              }
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Login color="primary" />
            </ListItemIcon>
            <ListItemText primary="Have an active LinkedIn account" />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
          Step-by-Step Instructions
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Extension color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Install Cookie Editor Extension"
              secondary="Go to the Chrome Web Store and install the Cookie Editor extension"
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <Login color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Log in to LinkedIn"
              secondary="Open LinkedIn.com in your browser and sign in to your LinkedIn account"
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <GetApp color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Export Your LinkedIn Cookies"
              secondary={
                <>
                  <Typography variant="body2" color="text.secondary">
                    Click on the Cookie Editor extension icon in your browser
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In the Cookie Editor panel, click the "Export" button
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select "JSON" format from the export options
                  </Typography>
                </>
              }
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <AddCircle color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Add the LinkedIn Account in the Dashboard"
              secondary={
                <>
                  <Typography variant="body2" color="text.secondary">
                    Navigate to the Accounts section (you are already here)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "Add Account" button
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paste the JSON cookies you exported from LinkedIn into the text field
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click "Save" to add your account
                  </Typography>
                </>
              }
            />
          </ListItem>
        </List>

        <Box sx={{ mt: 2, p: 2, bgcolor: '#fff8e1', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Info color="warning" sx={{ mr: 1 }} />
            Important Notes:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Do not click the 'Logout' button on LinkedIn after exporting cookies"
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Simply close your browser instead" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Your Account ID will appear in the accounts table after successful addition" />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CheckCircle color="success" sx={{ mr: 1 }} />
            Verification:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Check that your account appears in the accounts list" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Your account is now ready to use for LinkedIn operations" />
            </ListItem>
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Make sure the component is properly exported
export default LinkedInAccountGuide;